import React, { useState, useEffect, useCallback } from 'react';
import { chatsApi } from '../../api/chats';
import HrnbLoader from '../controls/HrnbLoader';
import '../../css/chats/BookmarksPanel.css';

/**
 * Панель закладок пользователя.
 * Отображает все закладки текущего пользователя с пагинацией.
 * При клике на закладку — навигация к сообщению в чате.
 *
 * @param {Function} onNavigate - (chatId, messageId) => void
 * @param {Function} onClose - () => void
 */
export default function BookmarksPanel({ onNavigate, onClose }) {
    const [bookmarks, setBookmarks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(false);

    // Загрузка первой страницы
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await chatsApi.getUserBookmarks({ page: 0, size: 50 });
                if (cancelled) return;
                setBookmarks(data.content || []);
                setHasMore((data.totalPages || 1) > 1);
                setPage(0);
            } catch (err) {
                if (!cancelled) setError('Не удалось загрузить закладки');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, []);

    // Загрузить ещё
    const handleLoadMore = useCallback(async () => {
        if (loadingMore || !hasMore) return;
        setLoadingMore(true);
        try {
            const nextPage = page + 1;
            const data = await chatsApi.getUserBookmarks({ page: nextPage, size: 50 });
            setBookmarks(prev => [...prev, ...(data.content || [])]);
            setHasMore((data.totalPages || 1) > nextPage + 1);
            setPage(nextPage);
        } catch (err) {
            setError('Не удалось загрузить ещё закладки');
        } finally {
            setLoadingMore(false);
        }
    }, [page, loadingMore, hasMore]);

    // Удалить закладку
    const handleRemove = useCallback(async (bookmark) => {
        if (!bookmark.chatId || !bookmark.messageId) return;
        // Оптимистичное удаление
        setBookmarks(prev => prev.filter(b => b.id !== bookmark.id));
        try {
            await chatsApi.removeBookmark(bookmark.chatId, bookmark.messageId);
        } catch (err) {
            // Возвращаем при ошибке
            setBookmarks(prev => {
                const restored = [...prev, bookmark];
                restored.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                return restored;
            });
            setError('Не удалось удалить закладку');
        }
    }, []);

    const handleClick = useCallback((bookmark) => {
        onNavigate?.(bookmark.chatId, bookmark.messageId);
        onClose?.();
    }, [onNavigate, onClose]);

    return (
        <div className="bookmarks-panel">
            <div className="bookmarks-panel__header">
                <h3 className="bookmarks-panel__title">
                    <span className="bookmarks-panel__title-icon">🔖</span>
                    Закладки
                </h3>
                <button
                    type="button"
                    className="bookmarks-panel__close"
                    onClick={onClose}
                    title="Закрыть"
                >
                    ✕
                </button>
            </div>

            <div className="bookmarks-panel__body">
                {loading && <HrnbLoader />}

                {error && (
                    <div className="bookmarks-panel__error">{error}</div>
                )}

                {!loading && !error && bookmarks.length === 0 && (
                    <div className="bookmarks-panel__empty">
                        <div className="bookmarks-panel__empty-icon">🔖</div>
                        <div className="bookmarks-panel__empty-text">
                            Нет закладок.
                            <br />
                            Нажмите «В закладки» в контекстном меню сообщения.
                        </div>
                    </div>
                )}

                {!loading && !error && bookmarks.length > 0 && (
                    <>
                        <div className="bookmarks-panel__list">
                            {bookmarks.map(bookmark => (
                                <div
                                    key={bookmark.id}
                                    className="bookmarks-panel__item"
                                    onClick={() => handleClick(bookmark)}
                                >
                                    <div className="bookmarks-panel__item-content">
                                        {bookmark.messageContent || '(нет текста)'}
                                    </div>
                                    <div className="bookmarks-panel__item-meta">
                                        <span className="bookmarks-panel__item-sender">
                                            {bookmark.sender?.displayName || 'Unknown'}
                                        </span>
                                        <span className="bookmarks-panel__item-date">
                                            {bookmark.sentAt
                                                ? new Date(bookmark.sentAt).toLocaleDateString([], {
                                                    day: '2-digit',
                                                    month: 'short',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                })
                                                : ''}
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        className="bookmarks-panel__item-remove"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRemove(bookmark);
                                        }}
                                        title="Удалить закладку"
                                    >
                                        ✕
                                    </button>
                                </div>
                            ))}
                        </div>
                        {hasMore && (
                            <button
                                type="button"
                                className="bookmarks-panel__load-more"
                                onClick={handleLoadMore}
                                disabled={loadingMore}
                            >
                                {loadingMore ? 'Загрузка…' : 'Загрузить ещё'}
                            </button>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
